'use client';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import noop from 'lodash/noop';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import ChevronDownIcon from '../../icons/ChevronDown';
import ChevronUpIcon from '../../icons/ChevronUp';
import { fontSmoothing, getFontFamily } from '../../typography';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import Box from '../Box';
import Button from '../Button';

export const styles = ({ idsTokens = idsTokensDefault }, { variant = 'classic' }) => ({
  buttonDefault: {
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
    boxSizing: 'border-box',
    height: idsTokens.navbar[variant].sizing.item.height,
    gap: idsTokens.navbar[variant].spacing.item.gap,
    paddingTop: idsTokens.navbar[variant].spacing.item.top,
    paddingBottom: idsTokens.navbar[variant].spacing.item.bottom,
    paddingLeft: idsTokens.navbar[variant].spacing.item.left,
    paddingRight: idsTokens.navbar[variant].spacing.item.right,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: idsTokens.navbar[variant].color.content.item.enabled,
    fontWeight: idsTokens.typography.font.weight.medium,
    fontSize: idsTokens.typography.font.size.base,
    lineHeight: idsTokens.typography.lineHeight.base,
    borderRadius: 0,
    background: idsTokens.navbar[variant].color.surface.item.enabled,
    border: '2px solid transparent',
    letterSpacing: idsTokens.typography.spacing.letter.lg,
    '& img': {
      maxHeight: 56,
    },
    '&:focus-visible': {
      outline: `${idsTokens.functional.borderWidth.focus.default} solid ${
        idsTokens.functional.color.border['focus-inverse']
      }`,
      outlineOffset: -2,
      color: idsTokens.navbar[variant].color.content.item.focus,
      background: idsTokens.navbar[variant].color.surface.item.focus,
    },
    '&:hover': {
      color: idsTokens.navbar[variant].color.content.item.hover,
      background: idsTokens.navbar[variant].color.surface.item.hover,
      border: '2px solid transparent',
    },
    '&:active': {
      color: idsTokens.navbar[variant].color.content.item.pressed,
      background: idsTokens.navbar[variant].color.surface.item.pressed,
    },
    '&:disabled': {
      pointerEvents: 'none',
      color: idsTokens.navbar[variant].color.content.item.disabled,
      background: idsTokens.navbar[variant].color.surface.item.disabled,
    },
  },
  buttonActive: {
    background: `${idsTokens.navbar[variant].color.surface.item.selected} !important`,
    color: idsTokens.navbar[variant].color.content.item.selected,
  },
  arrow: {
    width: idsTokens.navbar[variant].sizing.icon.width,
    height: idsTokens.navbar[variant].sizing.icon.height,
    boxSizing: 'content-box',
  },
  customComponent: {
    cursor: 'pointer',
    userSelect: 'none',
  },
});

const useStyles = makeStyles()(styles);

export const NavigationBarButton = ({
  href,
  onClick,
  src,
  icon,
  text,
  hasMenuItems,
  handleClick,
  open,
  active,
  className,
  variant = 'classic',
  hideArrow,
  onClose = noop,
  forwardedRef: ref,
  slots,
  slotProps,
  ...rest
}) => {
  const { classes, cx } = useStyles({ variant });
  return (
    <ClickAwayListener onClickAway={open ? onClose : noop}>
      <Box
        component={slots?.NavigationMenuItem ?? Button}
        {...slotProps?.NavigationMenuItem}
        className={cx(
          classes.buttonDefault,
          active && classes.buttonActive,
          slots?.NavigationMenuItem && classes.customComponent,
          className,
          slotProps?.NavigationMenuItem?.className
        )}
        href={href}
        onClick={(e) => {
          onClick && onClick();
          handleClick && handleClick(e);
        }}
        {...rest}
        ref={ref}
      >
        {src && <img src={src} alt="" />}
        {icon}
        {text}
        {hasMenuItems && !hideArrow && !open && <ChevronDownIcon className={classes.arrow} />}
        {hasMenuItems && !hideArrow && open && <ChevronUpIcon className={classes.arrow} />}
      </Box>
    </ClickAwayListener>
  );
};

export default withRef()(NavigationBarButton);
